import { useSelector } from 'react-redux';
import {
  RemoteConfig,
  activate,
  fetchAndActivate,
  getValue,
  onConfigUpdate,
  setCustomSignals,
} from 'firebase/remote-config';

import defaultConfig from '~/app/firebase/defaultConfig.json';
import { remoteConfig as getFirebaseRemoteConfig } from '~/app/firebase';
import store from '~/store/Store';
import logger from '~/utils/logger/logger';
import {
  ParameterType,
  RemoteConfigHookResult,
  RemoteConfigParameters,
  RemoteConfigPrimitive,
  RemoteConfigState,
  RemoteConfigValue,
  RemoteConfigValues,
} from './remote-config-types';
import { setRemoteConfigState } from './remote-config-slice';

const toPascalCase = (str: string) => {
  const camelCase = str.replaceAll(/([-_]\w)/g, (g) => g[1].toUpperCase());
  return camelCase.charAt(0).toUpperCase() + camelCase.slice(1);
};

// Absorbs transient WebView IndexedDB / network rejections (Sentry FLEET-WEB-MV / -MN / -SH).
const RETRY_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 500;

const fetchAndActivateWithRetry = async (rc: RemoteConfig): Promise<void> => {
  let lastError: unknown;
  for (let attempt = 0; attempt < RETRY_ATTEMPTS; attempt++) {
    try {
      await fetchAndActivate(rc);
      return;
    } catch (error) {
      lastError = error;
      if (attempt < RETRY_ATTEMPTS - 1) {
        const delay = RETRY_BASE_DELAY_MS * 2 ** attempt;
        logger.warn(
          'remote-config',
          `fetchAndActivate failed (attempt ${
            attempt + 1
          }/${RETRY_ATTEMPTS}), retrying in ${delay}ms`,
          error
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }
  throw lastError;
};

const dispatch = (state: RemoteConfigState) => store.dispatch(setRemoteConfigState(state));

export const createRemoteConfigApi = <P extends RemoteConfigParameters>(parameters: P) => {
  const readAllValues = (rc: RemoteConfig): Record<string, RemoteConfigValue> => {
    const values: Record<string, RemoteConfigValue> = {};
    for (const parameterName in parameters) {
      logger.debug(
        'remote-config',
        `${parameterName} =`,
        getValue(rc, parameters[parameterName].key).asString()
      );
      const config = parameters[parameterName];
      switch (config.type) {
        case 'boolean':
          values[parameterName] = getValue(rc, config.key).asBoolean();
          break;
        case 'string':
          values[parameterName] = getValue(rc, config.key).asString();
          break;
        case 'number':
          values[parameterName] = getValue(rc, config.key).asNumber();
          break;
      }
    }
    return values;
  };

  // Build initial snapshot from defaults
  const initialValues: Record<string, RemoteConfigValue> = {};
  for (const parameterName in parameters) {
    const config = parameters[parameterName];
    const defaultValue = defaultConfig[config.key];
    switch (config.type) {
      case 'boolean':
        initialValues[parameterName] = defaultValue === 'true';
        break;
      case 'number':
        initialValues[parameterName] = Number(defaultValue);
        break;
      default:
        initialValues[parameterName] = defaultValue;
    }
  }

  let hasFetched = false;
  let activeSignalKeys: string[] = [];

  const resetRemoteConfig = () => {
    hasFetched = false;
    // activeSignalKeys is intentionally preserved so the next fetchConfig call
    // knows which stale signals to clear from the RC instance.
    dispatch({ isLoaded: false, values: { ...initialValues } });
  };

  const fetchConfig = async (
    customSignals?: Record<string, string | number | null>
  ): Promise<(() => void) | undefined> => {
    if (hasFetched) {
      return;
    }
    hasFetched = true;

    const rc = await getFirebaseRemoteConfig();
    if (!rc) {
      hasFetched = false;
      dispatch({ isLoaded: true, values: { ...initialValues } });
      return;
    }

    // Always bypass Firebase's client-side IndexedDB cache on the initial fetch.
    // This prevents stale user-specific values from a previous session leaking into
    // a new one (e.g. after DIAM OIDC redirect which reloads the page and destroys
    // all in-memory state while IndexedDB persists).
    rc.settings.minimumFetchIntervalMillis = 0;
    rc.defaultConfig = defaultConfig;

    // Clear stale signals from a previous session, then apply new ones if provided.
    // setCustomSignals merges, so setting a key to null removes it from the RC instance.
    const signalsToClear: Record<string, null> = {};
    for (const key of activeSignalKeys) {
      if (!customSignals || !(key in customSignals)) {
        signalsToClear[key] = null;
      }
    }
    const mergedSignals = { ...signalsToClear, ...customSignals };
    if (Object.keys(mergedSignals).length > 0) {
      logger.debug('remote-config', 'Setting custom signals:', mergedSignals);
      await setCustomSignals(rc, mergedSignals);
    }
    activeSignalKeys = customSignals ? Object.keys(customSignals) : [];

    try {
      await fetchAndActivateWithRetry(rc);
      dispatch({ isLoaded: true, values: readAllValues(rc) });
    } catch (error) {
      logger.error('remote-config', 'Error fetching remote config:', error);
      dispatch({ isLoaded: true, values: { ...initialValues } });
    }

    // Subscribe to realtime config updates
    const unsubscribeUpdates = onConfigUpdate(rc, {
      next: () => {
        void activate(rc)
          .then(() => {
            const values = readAllValues(rc);
            dispatch({ isLoaded: true, values });
            logger.debug('remote-config', 'Realtime config update applied:', values);
          })
          .catch((error) => {
            logger.error('remote-config', 'Error applying remote config update:', error);
          });
      },
      error: (error) => {
        logger.warn('remote-config', 'Remote config update stream error:', error);
      },
      complete: () => {
        logger.warn('remote-config', 'Remote config update stream completed unexpectedly');
      },
    });

    return unsubscribeUpdates;
  };

  const refetchWithSignals = async (
    customSignals: Record<string, string | number | null>
  ): Promise<void> => {
    const rc = await getFirebaseRemoteConfig();
    if (!rc) {
      dispatch({ isLoaded: true, values: { ...initialValues } });
      return;
    }

    // Clear signals that were active but aren't in the new set
    const signalsToClear: Record<string, null> = {};
    for (const key of activeSignalKeys) {
      if (!(key in customSignals)) {
        signalsToClear[key] = null;
      }
    }
    const mergedSignals = { ...signalsToClear, ...customSignals };
    logger.debug('remote-config', 'Re-fetching with custom signals:', mergedSignals);
    await setCustomSignals(rc, mergedSignals);
    activeSignalKeys = Object.keys(customSignals);

    // Bypass cache so Firebase conditions re-evaluate with the new signals
    rc.settings.minimumFetchIntervalMillis = 0;

    try {
      await fetchAndActivateWithRetry(rc);
      dispatch({ isLoaded: true, values: readAllValues(rc) });
    } catch (error) {
      logger.error('remote-config', 'Error re-fetching remote config with signals:', error);
    }
  };

  const defaultState: RemoteConfigState = { isLoaded: false, values: {} };

  const useRemoteConfigState = (): RemoteConfigState & { values: RemoteConfigValues<P> } => {
    const state = useSelector((root: { remoteConfig?: RemoteConfigState }) => root?.remoteConfig);
    return (state ?? defaultState) as RemoteConfigState & { values: RemoteConfigValues<P> };
  };

  // For boolean params: if the name already ends in "Enabled" keep it as-is,
  // otherwise append "Enabled". Non-boolean params are never suffixed.
  type HookNameForParam<Name extends string> = P[Name]['type'] extends 'boolean'
    ? Name extends `${string}Enabled`
      ? `useGet${Capitalize<Name>}`
      : `useGet${Capitalize<Name>}Enabled`
    : `useGet${Capitalize<Name>}`;

  type Hooks = {
    [Name in keyof P as HookNameForParam<Name & string>]: () => RemoteConfigHookResult<
      RemoteConfigPrimitive<P[Name]['type']>
    >;
  };

  const hooks: Partial<Hooks> = {};
  for (const endpointName in parameters) {
    const config = parameters[endpointName];
    const pascalName = toPascalCase(endpointName);

    let hookName = `useGet${pascalName}`;
    if (config.type === 'boolean' && !hookName.endsWith('Enabled')) {
      hookName += 'Enabled';
    }

    (hooks as Record<string, () => RemoteConfigHookResult<RemoteConfigPrimitive<ParameterType>>>)[
      hookName
    ] = () => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { isLoaded, values } = useRemoteConfigState();
      return { data: values[endpointName] ?? initialValues[endpointName], isLoaded };
    };
  }

  return {
    hooks: hooks as unknown as Hooks,
    initRemoteConfig: fetchConfig,
    refetchWithSignals,
    resetRemoteConfig,
  };
};
